import { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { parseISO, format, parse } from 'date-fns';

import Loading from "../../../components/Loading";
import Data from '../../../../repository';
import { createMembershipPlanSchema } from '../../../../validation/schemas';

import styles from '../../../../styles/Admin.module.scss';

function UpdateMembershipPlanForm() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [applying, setApplying] = useState(false);
	const [serverError, setServerError] = useState(null);
	const [membershipPlan, setMembershipPlan] = useState(null);
	const [priceChangePrompt, setPriceChangePrompt] = useState(null); // { oldAmount, newAmount, count }

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		control
	} = useForm({
		resolver: yupResolver(createMembershipPlanSchema),
		defaultValues: {
			name: '',
			description: '',
			amount: '',
			interval_count: 1,
			interval: 'month',
			lockstateLockId: '',
			secondaryLockstateLockId: '',
			startTime: '00:00',
			endTime: '23:59',
			scheduleStartTime: '00:00',
			scheduleEndTime: '23:59',
			paymentType: 'both',
			active: true,
			maxPayments: '',
			subscriptionEndDate: '',
			tokensPerCycle: 0,
			openGymTokensPerCycle: 0,
			classTokensPerCycle: 0,
			tokenExpiryDays: ''
		}
	});

	useEffect(() => {
		fetchMembershipPlan();
	}, []);

	const fetchMembershipPlan = async () => {
		setLoading(true);
		try {
			const result = await Data.fetchMembershipPlan(id);
			const data = result.data;
			setMembershipPlan(data);
			reset({
				name: data.name || '',
				description: data.description || '',
				amount: data.amount || '',
				interval_count: data.interval_count || 1,
				interval: data.interval || 'month',
				lockstateLockId: data.lockstateLockId || '',
				secondaryLockstateLockId: data.secondaryLockstateLockId || '',
				startTime: data.startTime || '00:00',
				endTime: data.endTime || '23:59',
				scheduleStartTime: data.scheduleStartTime || '00:00',
				scheduleEndTime: data.scheduleEndTime || '23:59',
				paymentType: data.paymentType || 'both',
				active: data.active,
				maxPayments: data.maxPayments || '',
				subscriptionEndDate: data.subscriptionEndDate ? new Date(data.subscriptionEndDate).toISOString().split('T')[0] : '',
				tokensPerCycle: data.tokensPerCycle || 0,
				openGymTokensPerCycle: data.openGymTokensPerCycle || 0,
				classTokensPerCycle: data.classTokensPerCycle || 0,
				tokenExpiryDays: data.tokenExpiryDays || ''
			});
		} catch (error) {
			setServerError('Failed to load membership plan');
		} finally {
			setLoading(false);
		}
	}

	const onSubmit = async (data) => {
		setSaving(true);
		setServerError(null);

		const newAmount = Number(data.amount);
		const oldAmount = membershipPlan.amount;

		try {
			await Data.updateMembershipPlan({
				...membershipPlan,
				...data,
				amount: newAmount,
				interval_count: Number(data.interval_count),
				active: data.active === 'true' || data.active === true,
				maxPayments: data.maxPayments ? Number(data.maxPayments) : null,
				subscriptionEndDate: data.subscriptionEndDate || null,
				tokensPerCycle: data.tokensPerCycle ? Number(data.tokensPerCycle) : 0,
				openGymTokensPerCycle: data.openGymTokensPerCycle ? Number(data.openGymTokensPerCycle) : 0,
				classTokensPerCycle: data.classTokensPerCycle ? Number(data.classTokensPerCycle) : 0,
				tokenExpiryDays: data.tokenExpiryDays ? Number(data.tokenExpiryDays) : null
			});

			if (newAmount !== oldAmount) {
				const countResult = await Data.fetchMembershipPlanSubscriberCount(id);
				if (countResult.data.count > 0) {
					setPriceChangePrompt({
						oldAmount,
						newAmount,
						count: countResult.data.count
					});
					setSaving(false);
					return;
				}
			}

			navigate('/admin/membership-plans');
		} catch (error) {
			setServerError(error.response?.data?.message || 'Failed to update membership plan. Please try again.');
			setSaving(false);
		}
	}

	const handleApplyPrice = async () => {
		setApplying(true);
		try {
			await Data.applyPriceToSubscribers(id);
			navigate('/admin/membership-plans');
		} catch (error) {
			setServerError('Failed to apply price to subscribers. Please try again.');
			setApplying(false);
		}
	}

	if (loading) return <Loading button={false} />;

	if (priceChangePrompt) {
		const oldPounds = (priceChangePrompt.oldAmount / 100).toFixed(2);
		const newPounds = (priceChangePrompt.newAmount / 100).toFixed(2);
		return (
			<div className={styles.form}>
				<div style={{ gridColumn: '1 / -1' }}>
					<p style={{ fontWeight: 600, marginBottom: '8px' }}>Apply new price to existing subscribers?</p>
					<p>
						{priceChangePrompt.count} active subscriber{priceChangePrompt.count !== 1 ? 's are' : ' is'} currently
						being charged <strong>£{oldPounds}</strong> per payment. Would you like to update them
						to <strong>£{newPounds}</strong>? This will take effect on their next billing date.
					</p>
				</div>

				<div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', marginTop: '8px' }}>
					<button
						type="button"
						className="btn btnPrimary"
						onClick={handleApplyPrice}
						disabled={applying}
					>
						{applying ? <Loading button={true} /> : `Apply £${newPounds} to all ${priceChangePrompt.count} subscriber${priceChangePrompt.count !== 1 ? 's' : ''}`}
					</button>
					<button
						type="button"
						className="btn btnSecondary"
						onClick={() => navigate('/admin/membership-plans')}
						disabled={applying}
					>
						Leave existing subscribers on £{oldPounds}
					</button>
				</div>

				{serverError && <p style={{ color: 'red', gridColumn: '1 / -1' }}>{serverError}</p>}
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
			<div>
				<p>Name: *</p>
				<input
					type="text"
					{...register('name')}
					className={errors.name ? 'input-error' : ''}
				/>
				{errors.name && <span className="form-field-error">{errors.name.message}</span>}
			</div>

			<div>
				<p>Description:</p>
				<input type="text" {...register('description')} />
			</div>

			<div>
				<p>Amount (in pence): *</p>
				<input
					type="number"
					{...register('amount')}
					className={errors.amount ? 'input-error' : ''}
				/>
				{errors.amount && <span className="form-field-error">{errors.amount.message}</span>}
			</div>

			<div>
				<p>Interval: *</p>
				<div style={{ display: 'flex', gap: '10px' }}>
					<input
						type="number"
						{...register('interval_count')}
						min={1}
						max={365}
						style={{ width: '80px' }}
						className={errors.interval_count ? 'input-error' : ''}
					/>
					<select {...register('interval')}>
						<option value="day">Days</option>
						<option value="week">Weeks</option>
						<option value="month">Months</option>
						<option value="year">Years</option>
					</select>
				</div>
				{errors.interval_count && <span className="form-field-error">{errors.interval_count.message}</span>}
			</div>

			<div>
				<p>Lock ID:</p>
				<input type="text" {...register('lockstateLockId')} />
			</div>

			<div>
				<p>Secondary Lock ID:</p>
				<input type="text" {...register('secondaryLockstateLockId')} />
			</div>

			<div>
				<p>Schedule:</p>
				<div style={{ display: 'flex', gap: '10px' }}>
					<Controller
						name="startTime"
						control={control}
						render={({ field }) => (
							<TimePicker
								value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
								onChange={(date) => field.onChange(date ? format(date, 'HH:mm') : '')}
								ampm={false}
							/>
						)}
					/>
					<Controller
						name="endTime"
						control={control}
						render={({ field }) => (
							<TimePicker
								value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
								onChange={(date) => field.onChange(date ? format(date, 'HH:mm') : '')}
								ampm={false}
							/>
						)}
					/>
				</div>
			</div>

			<div>
				<p>Schedule (optional):</p>
				<div style={{ display: 'flex', gap: '10px' }}>
					<Controller
						name="scheduleStartTime"
						control={control}
						render={({ field }) => (
							<TimePicker
								value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
								onChange={(date) => field.onChange(date ? format(date, 'HH:mm') : '')}
								ampm={false}
							/>
						)}
					/>
					<Controller
						name="scheduleEndTime"
						control={control}
						render={({ field }) => (
							<TimePicker
								value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
								onChange={(date) => field.onChange(date ? format(date, 'HH:mm') : '')}
								ampm={false}
							/>
						)}
					/>
				</div>
			</div>

			<div>
				<p>Payment Type:</p>
				<select {...register('paymentType')}>
					<option value="both">Both (One-time & Subscription)</option>
					<option value="subscription">Subscription Only</option>
					<option value="one-time">One-time Only</option>
				</select>
			</div>

			<div>
				<p>Tokens Granted Per Payment:</p>
				<input
					type="number"
					{...register('tokensPerCycle')}
					min={0}
					className={errors.tokensPerCycle ? 'input-error' : ''}
				/>
				{errors.tokensPerCycle && <span className="form-field-error">{errors.tokensPerCycle.message}</span>}
				<span className={styles.helpText}>For hybrid gyms, grant this many tokens whenever the membership payment succeeds.</span>
			</div>

			<div>
				<p>Open Gym Tokens Granted Per Payment:</p>
				<input
					type="number"
					{...register('openGymTokensPerCycle')}
					min={0}
					className={errors.openGymTokensPerCycle ? 'input-error' : ''}
				/>
				{errors.openGymTokensPerCycle && <span className="form-field-error">{errors.openGymTokensPerCycle.message}</span>}
				<span className={styles.helpText}>For hybrid-new gyms, grant this many open gym tokens whenever the membership payment succeeds.</span>
			</div>

			<div>
				<p>Class Tokens Granted Per Payment:</p>
				<input
					type="number"
					{...register('classTokensPerCycle')}
					min={0}
					className={errors.classTokensPerCycle ? 'input-error' : ''}
				/>
				{errors.classTokensPerCycle && <span className="form-field-error">{errors.classTokensPerCycle.message}</span>}
				<span className={styles.helpText}>For hybrid-new gyms, grant this many class tokens whenever the membership payment succeeds.</span>
			</div>

			<div>
				<p>Token Expiry Days (optional):</p>
				<input
					type="number"
					{...register('tokenExpiryDays')}
					min={1}
					placeholder="e.g. 30"
					className={errors.tokenExpiryDays ? 'input-error' : ''}
				/>
				{errors.tokenExpiryDays && <span className="form-field-error">{errors.tokenExpiryDays.message}</span>}
				<span className={styles.helpText}>Leave blank if tokens from this plan should not expire.</span>
			</div>

			<div>
				<p>Max Payments (optional):</p>
				<input
					type="number"
					{...register('maxPayments')}
					min={1}
					placeholder="e.g. 6"
					className={errors.maxPayments ? 'input-error' : ''}
				/>
				{errors.maxPayments && <span className="form-field-error">{errors.maxPayments.message}</span>}
				<span className={styles.helpText}>Stop the subscription after this many payments. Leave blank for no limit.</span>
			</div>

			<div>
				<p>Subscription End Date (optional):</p>
				<Controller
					name="subscriptionEndDate"
					control={control}
					render={({ field }) => (
						<DatePicker
							value={field.value ? parseISO(field.value) : null}
							onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
							slotProps={{ textField: { className: errors.subscriptionEndDate ? 'input-error' : '' } }}
						/>
					)}
				/>
				{errors.subscriptionEndDate && <span className="form-field-error">{errors.subscriptionEndDate.message}</span>}
				<span className={styles.helpText}>Stop the subscription after this date. Leave blank for no end date.</span>
			</div>

			<div>
				<p>Status:</p>
				<select {...register('active')}>
					<option value="true">Enabled</option>
					<option value="false">Disabled</option>
				</select>
			</div>

			{serverError && <p style={{ color: 'red' }}>{serverError}</p>}

			<button type="submit" className="btn btnPrimary" disabled={saving}>
				{saving ?
					<Loading button={true} />
					:
					`Save Changes`
				}
			</button>
		</form>
	)
}

export default UpdateMembershipPlanForm;
